import React, { memo, useEffect, useState } from 'react';
import { setRadishAmount, useOfficialStore } from '$/store/useOfficialStore';
import { message } from '$/components/Message';
import { getSubscribe } from '$/common/utils/subscribeUtil';
import type { SubscriptionContract } from '@meitu/subscribe';
import { Popover } from 'antd';
import {
  isRequestNetworkError,
  notifyRequestNetworkErrorOnce,
} from '$/api/utils/resolveRequestErrorMessage';
import { trackRoboneoVipShow } from '$/utils/vipPromptTrack';
import type { VipMembershipMode } from '$/store/useVipModalStore';
import OverlayContent from './OverlayContent';
import Image from '../NextImage';
import calcIcon from './radish.png';
import styles from './index.module.scss';

interface CalcRadishProps {
  variant?: 'default' | 'teamFlowHeader' | 'homeHeader';
  hoverTriggerExtra?: React.ReactNode;
  vipMembershipMode?: VipMembershipMode;
}

// 渲染右上角算力萝卜入口，并在悬浮时刷新会员与续期信息。
function CalcRadish({ variant = 'default', hoverTriggerExtra, vipMembershipMode }: CalcRadishProps) {
  const [contractList, setContractList] = useState<SubscriptionContract[]>([]);
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const radishAmount = useOfficialStore((state) => state.radishAmount);
  const userInfo = useOfficialStore((state) => state.userInfo);
  const [hovered, setHovered] = useState(false);
  const loadSubscribeInfo = useOfficialStore(
    (state) => state.loadSubscribeInfo,
  );

  const hide = () => {
    setHovered(false);
  };

  // 查询订阅合约用于订阅管理，断网时统一降级为友好的网络提示，避免暴露订阅 SDK 接口地址。
  const fetchContractList = async () => {
    try {
      const instance = await getSubscribe();
      const { appId } = useOfficialStore.getState();
      const res = await instance?.getContract({
        appId,
        accountType: 1,
        accountId: Number(userInfo.uid),
        platform: 4,
      });
      if (res?.length) {
        setContractList(res as unknown as SubscriptionContract[]);
      } else {
        setContractList([]);
      }
    } catch (err: unknown) {
      if (isRequestNetworkError(err)) {
        notifyRequestNetworkErrorOnce(err, (key) => message.error(key));
        return;
      }
      if (err instanceof Error && err.message) {
        message.error(err.message);
      }
    }
  };

  useEffect(() => {
    if (!isUserLoggedIn) {
      setRadishAmount(0);
    }
  }, [isUserLoggedIn]);

  const showRadishEntry = isUserLoggedIn
    && (userInfo.subscribe?.isValid === false || !!userInfo.subscribe?.isValid);

  // 将业务方传入的会员入口纳入同一个 Popover 触发区域，避免相邻入口出现 hover 断层。
  const renderHoverTrigger = (showRadish: boolean) => {
    return (
      <div className={styles['hover-trigger']}>
        {showRadish ? (
          <div
            className={variant === 'teamFlowHeader'
              ? `${styles['calc-radish-container']} ${styles['calc-radish-container-team-flow']}`
              : `${styles['calc-radish-container']} ${variant === 'homeHeader' ? styles['calc-radish-container-home-header'] : ''}`}
          >
            <Image src={calcIcon} width={23} height={23} alt="" />
            <span className={styles['multiple-symbol']}>×</span>
            <div className={styles['remain-number']}>{radishAmount}</div>
          </div>
        ) : null}
        {hoverTriggerExtra}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {showRadishEntry
        ? (
          <Popover
            open={hovered}
            onOpenChange={async (visible) => {
              setHovered(visible);
              if (visible) {
                trackRoboneoVipShow('my_carrot_upgrade');
                loadSubscribeInfo();
                fetchContractList();
              }
            }}
            placement="bottomRight"
            trigger={['hover']}
            overlayStyle={{
              zIndex: 'calc(var(--zindex-fixed) + 1) !important',
            }}
            overlayClassName="resetPopoverAndArrow"
          // eslint-disable-next-line react/no-unstable-nested-components
            content={() => (
              <OverlayContent
                hide={hide}
                contractList={contractList || []}
                vipMembershipMode={vipMembershipMode}
              />
            )}
          >
            {renderHoverTrigger(true)}
          </Popover>
        ) : renderHoverTrigger(false)}
    </div>
  );
}
export default memo(CalcRadish);
